import type { EffectScope } from 'vue'
import { useMouseInElement } from '@vueuse/core'
import { effectScope, nextTick, ref, watchEffect } from 'vue'
import { pointerAimsAtDrawer } from './menu-aim'

export type SlideoverMenuId = string | null

const SLIDEOVER_SWITCH_DELAY_MS = 300
const SLIDEOVER_CLOSE_DELAY_MS = 150
const SLIDEOVER_AIM_RECHECK_MS = 60
export const NAV_DRAWER_MARKER = 'nav-rail-drawer'
const SLIDEOVER_ELEMENT_SELECTOR = `[data-el="${NAV_DRAWER_MARKER}"]`
const NAV_SLIDEOVER_TRIGGER_SELECTOR = '[data-nav-slideover]:is(:hover, :focus-within)'

interface SlideoverChangeOptions {
  event: Event
  slideover: SlideoverMenuId
}

export const slideoverMenu = ref<SlideoverMenuId>(null)
let slideoverScope: EffectScope | null = null
let pendingSwitchTimeout: ReturnType<typeof setTimeout> | null = null
let pendingCloseTimeout: ReturnType<typeof setTimeout> | null = null

let mouseLoc = { x: 0, y: 0 }
let mousePrevLoc = { x: 0, y: 0 }
let aimTracking = false
let menuAimEnabled = false

export function setMenuAimEnabled(value: boolean) {
  menuAimEnabled = value
  if (!value) {
    stopAimTracking()
    mouseLoc = { x: 0, y: 0 }
    mousePrevLoc = { x: 0, y: 0 }
  }
}

function trackMouse(event: MouseEvent) {
  mousePrevLoc = mouseLoc
  mouseLoc = { x: event.clientX, y: event.clientY }
}

function startAimTracking() {
  if (!menuAimEnabled || aimTracking || typeof document === 'undefined') {
    return
  }
  document.addEventListener('mousemove', trackMouse, { passive: true })
  aimTracking = true
}

function stopAimTracking() {
  if (!aimTracking || typeof document === 'undefined') {
    return
  }
  document.removeEventListener('mousemove', trackMouse)
  aimTracking = false
}

function isMovingTowardDrawer() {
  if (!menuAimEnabled || typeof document === 'undefined') {
    return false
  }
  const drawer = document.querySelector<HTMLElement>(SLIDEOVER_ELEMENT_SELECTOR)
  if (!drawer) {
    return false
  }
  const aiming = pointerAimsAtDrawer(mouseLoc, mousePrevLoc, drawer.getBoundingClientRect())
  mousePrevLoc = mouseLoc
  return aiming
}

function cancelPendingSwitch() {
  if (pendingSwitchTimeout) {
    clearTimeout(pendingSwitchTimeout)
    pendingSwitchTimeout = null
  }
}

function cancelPendingClose() {
  if (pendingCloseTimeout) {
    clearTimeout(pendingCloseTimeout)
    pendingCloseTimeout = null
  }
}

function findActiveDrawerTrigger() {
  if (typeof document === 'undefined') {
    return null
  }
  return document.querySelector<HTMLElement>(NAV_SLIDEOVER_TRIGGER_SELECTOR)
}

function drawerHasFocus() {
  if (typeof document === 'undefined') {
    return false
  }
  return document.querySelector<HTMLElement>(SLIDEOVER_ELEMENT_SELECTOR)?.matches(':focus-within') ?? false
}

function scheduleClose(currentId: SlideoverMenuId, stillOutside: () => boolean) {
  cancelPendingClose()
  const attempt = () => {
    pendingCloseTimeout = null
    if (slideoverMenu.value !== currentId || pendingSwitchTimeout || !stillOutside()) {
      return
    }
    if (drawerHasFocus() || isMovingTowardDrawer()) {
      pendingCloseTimeout = setTimeout(attempt, SLIDEOVER_AIM_RECHECK_MS)
      return
    }
    const activeTrigger = findActiveDrawerTrigger()
    const activeId = activeTrigger?.getAttribute('data-nav-slideover')
    if (activeTrigger && activeId) {
      if (activeId === currentId) {
        return
      }
      scheduleSwitch(activeId, activeTrigger)
      return
    }
    closeSlideover()
  }
  pendingCloseTimeout = setTimeout(attempt, SLIDEOVER_CLOSE_DELAY_MS)
}

function scheduleSwitch(slideover: SlideoverMenuId, target: HTMLElement) {
  cancelPendingClose()
  cancelPendingSwitch()
  const attempt = () => {
    pendingSwitchTimeout = null
    if (isMovingTowardDrawer()) {
      pendingSwitchTimeout = setTimeout(attempt, SLIDEOVER_AIM_RECHECK_MS)
      return
    }
    openSlideover(slideover, target)
  }
  pendingSwitchTimeout = setTimeout(attempt, SLIDEOVER_SWITCH_DELAY_MS)
}

export const closeSlideoverOnMouseover: Record<string, (event: MouseEvent) => void> = {
  mouseenter: (event: MouseEvent) => handleSlideoverChange({ event, slideover: null }),
}

function cleanupSlideoverScope() {
  if (slideoverScope) {
    slideoverScope.stop()
    slideoverScope = null
  }
}

export function closeSlideover() {
  cleanupSlideoverScope()
  slideoverMenu.value = null
  cancelPendingSwitch()
  cancelPendingClose()
  stopAimTracking()
}

function setupHoverDetection(
  menuItemElement: HTMLElement | null,
  slideover: HTMLElement,
  currentSlideoverId: SlideoverMenuId,
) {
  if (!menuItemElement) {
    return
  }
  const { isOutside: isOutsideMenuItem } = useMouseInElement(menuItemElement)
  const { isOutside: isOutsideSlideover } = useMouseInElement(slideover)
  let hasBeenInsideOnce = false
  watchEffect(() => {
    const isOutsideBoth = isOutsideMenuItem.value && isOutsideSlideover.value
    const isInSubmenu = !isOutsideSlideover.value && isOutsideMenuItem.value
    if (!isOutsideBoth) {
      hasBeenInsideOnce = true
    }
    if (isInSubmenu) {
      cancelPendingSwitch()
    }
    if (isOutsideBoth && hasBeenInsideOnce) {
      scheduleClose(currentSlideoverId, () => isOutsideMenuItem.value && isOutsideSlideover.value)
    } else {
      cancelPendingClose()
    }
  })
}

async function openSlideover(slideoverId: SlideoverMenuId, target: HTMLElement) {
  slideoverMenu.value = slideoverId
  cancelPendingClose()
  startAimTracking()
  await nextTick()
  if (slideoverMenu.value !== slideoverId) {
    return
  }
  cleanupSlideoverScope()
  slideoverScope = effectScope()
  slideoverScope?.run(() => {
    const slideover = document.querySelector<HTMLElement>(SLIDEOVER_ELEMENT_SELECTOR)
    if (!slideover) {
      return
    }
    setupHoverDetection(target, slideover, slideoverId)
  })
}

export function handleSlideoverChange(options: SlideoverChangeOptions) {
  if (!(options.event.currentTarget instanceof HTMLElement)) {
    return
  }
  const target = options.event.currentTarget
  if (options.slideover) {
    if (!slideoverMenu.value) {
      cancelPendingSwitch()
      openSlideover(options.slideover, target)
      return
    }
    if (options.slideover === slideoverMenu.value) {
      cancelPendingSwitch()
      cancelPendingClose()
      return
    }
    scheduleSwitch(options.slideover, target)
  } else {
    cancelPendingSwitch()
    if (!slideoverMenu.value) {
      return
    }
    scheduleClose(slideoverMenu.value, () => true)
  }
}
